import { createContext, useContext, type ReactNode } from "react";
import type { WidgetDimension } from "./use-dimension-counts";
import type { Omittable } from "@salestrak/ui";

/** The date column a time-bucket drilldown filters on — mirrors each reader's
 *  `measure` → column mapping (`created` → creationDate, `updated` → lastUpdateDate). */
export type WidgetSliceDateField = "creationDate" | "lastUpdateDate";

/**
 * One clicked chart element, described as data. Three shapes, each carrying a
 * `label` for the Data tab's filter chip:
 *  - `dimension` — a category in a grouped chart (pie slice, bar, battery, map
 *    tile): the dimension the widget grouped by + the category ids the element
 *    covers (several when it's a folded "Other" slice).
 *  - `dateRange` — a time bucket (line point, heatmap day): rows whose `dateField`
 *    falls in `[startMs, endMs)`, optionally narrowed to a dimension category
 *    (e.g. the stage of a clicked per-stage line).
 *  - `outcomeActivity` — Project Update events narrowed by outcome and/or actor.
 *  - `issues` — an explicit set of projects, for a figure whose rows no issue
 *    predicate reproduces: the aggregation that produced it names the projects
 *    (posted Updates per project), so the drill lists exactly them. Exact on
 *    its own — the ids already carry the widget's scope — so a consumer must
 *    not AND a scope fold onto it: an actor's Update on a project they are not
 *    assigned to counted, and an assignee fold would drop its row.
 */
export type WidgetDataSlice =
  | { kind: "dimension"; dimension: WidgetDimension; ids: string[]; label: string }
  // A caller computes `dimension`/`ids` conditionally — an ungrouped line has
  // neither — and nothing reads an absent key differently from an explicit
  // `undefined`, so the member relaxes.
  | Omittable<{
      kind: "dateRange";
      dateField: WidgetSliceDateField;
      /** Inclusive lower bound (ms). `-Infinity` for a cumulative point ("up to here"). */
      startMs: number;
      /** Exclusive upper bound (ms) — the next bucket's start. */
      endMs: number;
      /** Optional extra category constraint (e.g. the stage of the clicked line). */
      dimension?: WidgetDimension;
      ids?: string[];
      label: string;
    }>
  | Omittable<{
      kind: "outcomeActivity";
      outcomeIds?: string[];
      actorIds?: string[];
      /** The entity kinds whose Updates the target counted (`ActivityEntityType`
       *  values) — the Update outcomes card's per-entity rows drill into
       *  one kind at a time. Absent = every kind. */
      entityTypes?: string[];
      /** Count already shown by the clicked widget target. Inline drilldowns use
       *  it to reserve their final table height before the query returns. */
      total?: number;
      label: string;
    }>
  | {
      kind: "issues";
      /** The exact rows. Empty when the figure is zero — there is no server
       *  predicate for an empty set (an empty `in` is ignored, not matched
       *  against nothing), so a consumer renders the empty state itself
       *  instead of paging. */
      ids: string[];
      label: string;
    };

export type WidgetDrilldownActions = {
  /** Open the widget detail dialog on its Data tab for the placed widget with
   *  this id. Pass a `slice` to filter the rows to the clicked element; pass
   *  `null` to open the full, unfiltered data. */
  openData: (instanceId: string, slice: WidgetDataSlice | null) => void;
  /** Open the widget detail dialog on its chart-only presentation — the
   *  widget body alone fills the dialog, no Settings / Data drawer. For a
   *  body that discloses its own drill in place, this is the inline table
   *  at dialog size. */
  openChart: (instanceId: string) => void;
};

/**
 * Click-through channel from a chart element to the Data drill-down. The
 * `Dashboard` host provides it (it owns the detail dialog and resolves the id
 * against its live widget list); a widget body pairs it with the kit's
 * `useWidgetInstance()` to address its own placement:
 *
 *   const drill = useWidgetDrilldown();
 *   const instance = useWidgetInstance();
 *   … onArcPress={() => instance && drill?.openData(instance.instanceId, slice)}
 */
const WidgetDrilldownContext = createContext<WidgetDrilldownActions | null>(null);

export function WidgetDrilldownProvider({
  value,
  children,
}: {
  value: WidgetDrilldownActions;
  children: ReactNode;
}) {
  return (
    <WidgetDrilldownContext.Provider value={value}>{children}</WidgetDrilldownContext.Provider>
  );
}

/** Null where no drilldown host is mounted — widgets no-op their click-through. */
export function useWidgetDrilldown(): WidgetDrilldownActions | null {
  return useContext(WidgetDrilldownContext);
}
